import { broadcastEmote } from '../services/worldBroadcast';
import { getCurrentWorldId } from '../services/realtimeManager';
import { useWorldStore } from '../stores/worldStore';
import React, { useEffect, useState } from 'react';
import { Smile } from 'lucide-react';
import { EMOTES, EMOTE_MAP, LIBRARY_EMOTES, LIBRARY_CATEGORIES, SIT_STYLES, SIT_EMOTES, setSitStyle } from '../config/emotes';
import './EmoteWheel.css';

interface EmoteWheelProps {
  // Fired when the player picks an emote (button or hotkey). Caller triggers the
  // local avatar and broadcasts to other players.
  onEmote: (name: string) => void;
  /** Optional controlled open state (the compact HUD's Emote button drives it). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// Number-key order: 1-9 map to emotes 0-8, 0 maps to emote 10 (index 9).
const HOTKEY_ORDER = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

/** True when the user is typing into a text field — hotkeys must be ignored. */
function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
}

const EmoteWheel: React.FC<EmoteWheelProps> = ({ onEmote, open: openProp, onOpenChange }) => {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  // Functional updates read the latest value through a ref (hotkeys bind once).
  const openRef = React.useRef(open);
  openRef.current = open;
  const setOpen = (v: boolean | ((o: boolean) => boolean)) => {
    const next = typeof v === 'function' ? v(openRef.current) : v;
    openRef.current = next;
    setOpenState(next);
    onOpenChange?.(next);
  };
  // 'mine' = the wheel's own motions; otherwise a Tripo library category.
  const [tab, setTab] = useState('mine');
  const tabs = ['mine', ...LIBRARY_CATEGORIES.filter((c) => LIBRARY_EMOTES.some((e) => e.category === c))];
  const shown = tab === 'mine' ? EMOTES : LIBRARY_EMOTES.filter((e) => e.category === tab);
  const active = useWorldStore(s => s.localEmote?.name);
  const stop = () => {
    const state = useWorldStore.getState();
    state.stopLocalEmote();
    const wid = state.actualWorldId || getCurrentWorldId();
    if (wid) broadcastEmote(wid, null);
  };

  const trigger = (name: string) => {
    onEmote(name);
    // Keep the panel open for looping dances so players can chain/switch;
    // close after a one-shot emote.
    const def = EMOTE_MAP[name];
    if (def && !def.loop) setOpen(false);
  };

  // Hotkeys: 1-9 / 0 fire the matching emote, "B" (boogie) toggles the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      const key = e.key.toLowerCase();
      if (key === 'b') {
        setOpen((o) => !o);
        return;
      }
      const idx = HOTKEY_ORDER.indexOf(e.key);
      if (idx >= 0 && idx < EMOTES.length) {
        trigger(EMOTES[idx].name);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While sitting, offer the other sitting styles; the pick is remembered and
  // used the next time a seat is clicked.
  const sitting = !!active && SIT_EMOTES.has(active);
  const chooseSitStyle = (emote: string) => {
    setSitStyle(emote);
    if (emote !== active) onEmote(emote);
  };

  return (
    <div className="emote-wheel">
      {sitting && (
        <div className="sit-styles" role="radiogroup" aria-label="Sitting style">
          {SIT_STYLES.map((st) => (
            <button
              key={st.emote}
              role="radio"
              aria-checked={active === st.emote}
              className={`sit-styles__opt ${active === st.emote ? 'on' : ''}`}
              onClick={() => chooseSitStyle(st.emote)}
              title={`Sit: ${st.label}`}
            >
              <img className="sit-styles__icon" src={st.icon} alt="" aria-hidden /> {st.label}
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="emote-wheel__panel">
          <div className="emote-wheel__heading"><strong>{tab === 'mine' ? `My motions · ${EMOTES.length}` : `${tab[0].toUpperCase()}${tab.slice(1)} · ${shown.length}`}</strong><button onClick={stop} disabled={!active}>Stop motion</button></div>
          {tabs.length > 1 && (
            <div className="emote-wheel__tabs" role="tablist" aria-label="Motion sets">
              {tabs.map((t) => (
                <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
                  {t === 'mine' ? 'Favorites' : `${t[0].toUpperCase()}${t.slice(1)}`}
                </button>
              ))}
            </div>
          )}
          {shown.map((emote, i) => {
            const key = tab === 'mine' ? HOTKEY_ORDER[i] : undefined;
            return (
              <button
                key={emote.name}
                className="emote-wheel__item"
                title={`${emote.label}${key ? ` (${key})` : ''}${emote.name === 'hug' || emote.name === 'high_five' ? ' — individual gesture' : ''}`}
                aria-pressed={active === emote.name}
                onClick={() => trigger(emote.name)}
              >
                <span className="emote-wheel__item-emoji">
                  {emote.icon ? <img className="emote-wheel__item-icon" src={emote.icon} alt="" aria-hidden /> : emote.emoji}
                </span>
                <span className="emote-wheel__item-label">{emote.label}</span>
                {key && <span className="emote-wheel__item-key">{key}</span>}
              </button>
            );
          })}
          <div className="emote-wheel__hint">Move to stop · 1–0 shortcuts · B to toggle</div>
        </div>
      )}
      <button
        className={`emote-wheel__trigger ${open ? 'open' : ''}`}
        title="Emotes (B)"
        aria-label="Emotes"
        onClick={() => setOpen((o) => !o)}
      >
        {Smile ? <Smile size={24} /> : <span className="emote-wheel__trigger-emoji">🕺</span>}
      </button>
    </div>
  );
};

export default EmoteWheel;
